// BracketEditor — chart-as-control React island for a `bracket_edit` group.
//
// A progressive-rate staircase where each editable boundary handle is
// paired with the rate that applies above it. Horizontal drag moves a
// bracket boundary; vertical drag moves the marginal rate of the
// bracket starting at that boundary. The leftmost handle is anchored
// at x = 0 (the leftmost rate has no editable boundary below it; the
// engine stores the implicit lower bound at zero).
//
// Architecture (cross-checked against react-graph-gallery + Red Blob
// Games — see plan, references inline below):
//   - React owns every SVG element via JSX. We never use
//     d3.select(...).append(...). Drag handlers call setState; React
//     re-renders, including the new circle positions.
//   - D3 owns scales (d3-scale) and drag behaviour (d3-drag) only.
//     scaleLinear().clamp(true) gives free chart-bounds clamping on
//     .invert() — defence in depth for the explicit snap + monotonic
//     clamp below.
//   - Compositional setter pattern: each constraint is a pure function
//     applied in order on every drag tick: chartClamp (free via scale)
//     → snap (round to grid step) → monotonic (clamp against current
//     state's neighbouring boundaries).
//   - Staircase rendered via line().curve(curveStepAfter). Synthetic
//     path points include a tail at (boundary_max, last_rate) so the
//     rightmost bracket has a visible horizontal segment even though
//     its upper bound is conceptually Inf in the engine.
//
// Numeric mirror beneath the chart edits the same state used by the
// chart. Same clamps applied on the mirror's input blur so typed
// values can't violate the same invariants the chart enforces.

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { scaleLinear } from "d3-scale";
import { line, curveStepAfter } from "d3-shape";
import { drag } from "d3-drag";
import { select } from "d3-selection";
import type { BracketEditorProps } from "./types";
import { useResetAllSignal } from "../../state/reset_signal";
import { recordControl } from "../../state/share_store";
import type { ControlMeta } from "../../state/share_store";
import { notifyRestored, restoredBrackets } from "../../state/share_restore";
import { InfoIcon } from "../info_icon";

interface State {
  boundaries: number[];
  rates: number[];
}

/** Cell that decouples the in-progress draft string from the committed
 *  canonical value. Live validation (red underline) fires on every
 *  keystroke; the commit (snap + monotonic clamp via the parent's
 *  onCommit) only fires on blur or Enter. Escape reverts the draft.
 *  This is what stops the input "fighting the typing" — the previous
 *  shape snapped on every keystroke, so 15200 → 1 was rendered as 100. */
interface NumberCellProps {
  value: number;
  min: number;
  max: number;
  format: (v: number) => string;
  parse: (s: string) => number;
  onCommit: (v: number) => void;
  testId: string;
  ariaLabel: string;
}

function NumberCell({
  value,
  min,
  max,
  format,
  parse,
  onCommit,
  testId,
  ariaLabel,
}: NumberCellProps): JSX.Element {
  const [draft, setDraft] = useState(format(value));

  // Re-sync the draft when the canonical value changes from the outside
  // (chart drag, reset, sibling commit clamping us). Without this the
  // input would stay frozen at the user's last typed value while the
  // chart shows the snapped value.
  useEffect(() => {
    setDraft(format(value));
  }, [value, format]);

  const parsed = parse(draft);
  const isInvalid =
    draft.trim() === "" || Number.isNaN(parsed) || parsed < min || parsed > max;

  const commit = () => {
    if (isInvalid) {
      setDraft(format(value));
      return;
    }
    onCommit(parsed);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      data-testid={testId}
      aria-label={ariaLabel}
      aria-invalid={isInvalid || undefined}
      className={`beamm-bracket-editor__mirror-input${
        isInvalid ? " beamm-bracket-editor__mirror-input--invalid" : ""
      }`}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          commit();
          (e.currentTarget as HTMLInputElement).blur();
        } else if (e.key === "Escape") {
          setDraft(format(value));
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
    />
  );
}

const WIDTH = 360;
const HEIGHT = 220;
const MARGIN = { top: 16, right: 16, bottom: 32, left: 56 };

export function BracketEditor({
  inputId,
  label,
  instrument,
  description,
  boundaryTargets,
  boundaryDefaults,
  rateDefaults,
  boundaryMin,
  boundaryMax,
  boundaryStep,
  rateMin,
  rateMax,
  rateStep,
  xAxisLabel,
  yAxisLabel,
}: BracketEditorProps): JSX.Element {
  const [state, setState] = useState<State>(() =>
    restoredBrackets(
      inputId,
      boundaryDefaults,
      rateDefaults,
      boundaryMin,
      boundaryMax,
      rateMin,
      rateMax,
    ),
  );

  // Which handle is currently being interacted with (drag or keyboard).
  // Drives the floating readout next to the active handle; cleared on
  // drag end and on blur.
  const [activeHandle, setActiveHandle] = useState<number | null>(null);

  // Static display metadata for the "Your reform" modal (#65); the modal
  // expands a bracket reform to one row per changed rate / boundary using the
  // defaults registered here.
  const meta = useMemo<ControlMeta>(
    () => ({ kind: "bracket", label, instrument, boundaryDefaults, rateDefaults }),
    [label, instrument, boundaryDefaults, rateDefaults],
  );

  // Push commit to Shiny on every state change. `priority: "deferred"`
  // matches CurveEditor: the server-side panel reactive sees the latest
  // value at Shiny's natural flush boundary, not per-tick, and the
  // engine still only runs on Simulate.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.Shiny?.setInputValue?.(
      inputId,
      { boundaries: state.boundaries, rates: state.rates },
      { priority: "deferred" },
    );
    // Mirror into the share registry for the Copy-link button (#244) and the
    // reform modal (#65). Compute modified inline — the memoised `isModified`
    // is declared further down.
    const modified =
      state.boundaries.some((b, i) => b !== boundaryDefaults[i]) ||
      state.rates.some((r, i) => r !== rateDefaults[i]);
    recordControl(
      inputId,
      { boundaries: state.boundaries, rates: state.rates },
      modified,
      meta,
    );
    // Report a restored control so the shared reform auto-runs once every
    // control has committed (#297). No-op unless this id was restored.
    notifyRestored(inputId);
  }, [inputId, state, boundaryDefaults, rateDefaults, meta]);

  const xScale = useMemo(
    () =>
      scaleLinear()
        .domain([0, boundaryMax])
        .range([MARGIN.left, WIDTH - MARGIN.right])
        .clamp(true),
    [boundaryMax],
  );

  const yScale = useMemo(
    () =>
      scaleLinear()
        .domain([rateMin, rateMax])
        .range([HEIGHT - MARGIN.bottom, MARGIN.top])
        .clamp(true),
    [rateMin, rateMax],
  );

  const pathGen = useMemo(
    () =>
      line<[number, number]>()
        .x((d) => xScale(d[0]))
        .y((d) => yScale(d[1]))
        .curve(curveStepAfter),
    [xScale, yScale],
  );

  // Compositional setter pieces. chartClamp is free via the scales'
  // clamp(true); snap + monotonic are explicit here so each step is
  // unit-testable in isolation if we extract them later.
  const snapBoundary = useCallback(
    (v: number) => Math.round(v / boundaryStep) * boundaryStep,
    [boundaryStep],
  );
  const snapRate = useCallback(
    (v: number) => Math.round(v / rateStep) * rateStep,
    [rateStep],
  );

  const gRef = useRef<SVGGElement | null>(null);

  // Attach d3-drag once per scale change. Handlers read current state
  // via setState's functional form, so we don't need `state` in deps —
  // re-attaching the behaviour on every state change would tear down
  // the drag mid-gesture.
  useEffect(() => {
    if (!gRef.current) return;
    const sel = select(gRef.current);

    const dragBehaviour = drag<SVGCircleElement, number>()
      .container(function () {
        return this.ownerSVGElement as SVGSVGElement;
      })
      .on("start", (_event, i) => {
        setActiveHandle(i);
      })
      .on("end", () => {
        setActiveHandle(null);
      })
      .on("drag", (event, i) => {
        // i is the handle index, bound below via .data([...]).
        const rawY = yScale.invert(event.y);
        const newY = Math.min(Math.max(snapRate(rawY), rateMin), rateMax);

        setState((s) => {
          const nextRates = [...s.rates];
          nextRates[i] = newY;

          // Leftmost handle (i=0) is fixed in x — it represents the
          // leftmost rate (`tarief_1`), anchored at the chart origin.
          if (i === 0) {
            return { boundaries: s.boundaries, rates: nextRates };
          }

          // Handle i > 0 also moves boundary at index (i - 1).
          const bIdx = i - 1;
          const rawX = xScale.invert(event.x);
          const snappedX = snapBoundary(rawX);
          // Monotonic clamp against current siblings — uses the
          // *current* state, not defaults, so the user can re-order
          // gradually without one move clobbering another.
          const lower =
            bIdx === 0
              ? boundaryMin + boundaryStep
              : s.boundaries[bIdx - 1] + boundaryStep;
          const upper =
            bIdx === s.boundaries.length - 1
              ? boundaryMax - boundaryStep
              : s.boundaries[bIdx + 1] - boundaryStep;
          const newX = Math.min(Math.max(snappedX, lower), upper);

          const nextBoundaries = [...s.boundaries];
          nextBoundaries[bIdx] = newX;
          return { boundaries: nextBoundaries, rates: nextRates };
        });
      });

    const handleCount = boundaryTargets.length + 1;
    const indices = Array.from({ length: handleCount }, (_, i) => i);
    sel
      .selectAll<
        SVGCircleElement,
        number
      >("circle.beamm-bracket-editor__handle--reform")
      .data(indices)
      .call(dragBehaviour);
  }, [
    xScale,
    yScale,
    snapBoundary,
    snapRate,
    boundaryMin,
    boundaryMax,
    boundaryStep,
    rateMin,
    rateMax,
    boundaryTargets.length,
  ]);

  const baselinePoints = useMemo<Array<[number, number]>>(
    () => [
      [0, rateDefaults[0]],
      ...boundaryDefaults.map(
        (b, i) => [b, rateDefaults[i + 1]] as [number, number],
      ),
      [boundaryMax, rateDefaults[rateDefaults.length - 1]],
    ],
    [boundaryDefaults, rateDefaults, boundaryMax],
  );

  const reformPoints = useMemo<Array<[number, number]>>(
    () => [
      [0, state.rates[0]],
      ...state.boundaries.map(
        (b, i) => [b, state.rates[i + 1]] as [number, number],
      ),
      [boundaryMax, state.rates[state.rates.length - 1]],
    ],
    [state, boundaryMax],
  );

  const handlePositions = useMemo(
    () => [
      { x: 0, y: state.rates[0] },
      ...state.boundaries.map((b, i) => ({ x: b, y: state.rates[i + 1] })),
    ],
    [state],
  );

  const baselineHandlePositions = useMemo(
    () => [
      { x: 0, y: rateDefaults[0] },
      ...boundaryDefaults.map((b, i) => ({ x: b, y: rateDefaults[i + 1] })),
    ],
    [boundaryDefaults, rateDefaults],
  );

  const isModified = useMemo(
    () =>
      state.boundaries.some((b, i) => b !== boundaryDefaults[i]) ||
      state.rates.some((r, i) => r !== rateDefaults[i]),
    [state, boundaryDefaults, rateDefaults],
  );

  const resetAll = useCallback(
    () =>
      setState({
        boundaries: [...boundaryDefaults],
        rates: [...rateDefaults],
      }),
    [boundaryDefaults, rateDefaults],
  );
  // Page-level "Reset all" also resets this island (it isn't a Shiny widget,
  // so the server-side reset can't reach it). #132.
  useResetAllSignal(resetAll);

  const onBoundaryCellChange = (idx: number, raw: number) => {
    setState((s) => {
      const snapped = snapBoundary(raw);
      const lower =
        idx === 0
          ? boundaryMin + boundaryStep
          : s.boundaries[idx - 1] + boundaryStep;
      const upper =
        idx === s.boundaries.length - 1
          ? boundaryMax - boundaryStep
          : s.boundaries[idx + 1] - boundaryStep;
      const clamped = Math.min(Math.max(snapped, lower), upper);
      const next = [...s.boundaries];
      next[idx] = clamped;
      return { ...s, boundaries: next };
    });
  };

  const onRateCellChange = (idx: number, raw: number) => {
    setState((s) => {
      const snapped = snapRate(raw);
      const clamped = Math.min(Math.max(snapped, rateMin), rateMax);
      const next = [...s.rates];
      next[idx] = clamped;
      return { ...s, rates: next };
    });
  };

  const xTicks = useMemo(() => xScale.ticks(5), [xScale]);
  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  // Brackets where the rate steps DOWN compared to the bracket below.
  // The chart-as-control intentionally allows this (only boundaries are
  // hard-clamped monotonic, not rates), but a casual reader sees a
  // downward step and wonders if something broke. We surface a short
  // note so the user knows we know.
  const nonMonotonicAt = useMemo(() => {
    const flagged: number[] = [];
    for (let i = 1; i < state.rates.length; i++) {
      if (state.rates[i] < state.rates[i - 1]) flagged.push(i + 1);
    }
    return flagged;
  }, [state.rates]);

  // Keyboard support on focused handles. Same compositional setter as
  // drag, with the delta coming from arrow keys instead of pointer
  // motion. ↑ / ↓ change the rate (handle i's rate); ← / → change the
  // boundary (handle 0 ignores horizontal since it's anchored at x=0).
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<SVGCircleElement>, i: number) => {
      const key = event.key;
      const isHorizontal = key === "ArrowLeft" || key === "ArrowRight";
      const isVertical = key === "ArrowUp" || key === "ArrowDown";
      if (!isHorizontal && !isVertical) return;
      event.preventDefault();
      setActiveHandle(i);
      setState((s) => {
        const nextRates = [...s.rates];
        const nextBoundaries = [...s.boundaries];
        if (isVertical) {
          const delta = key === "ArrowUp" ? rateStep : -rateStep;
          const candidate = nextRates[i] + delta;
          nextRates[i] = Math.min(Math.max(candidate, rateMin), rateMax);
        }
        if (isHorizontal && i > 0) {
          const bIdx = i - 1;
          const delta = key === "ArrowRight" ? boundaryStep : -boundaryStep;
          const candidate = nextBoundaries[bIdx] + delta;
          const lower =
            bIdx === 0
              ? boundaryMin + boundaryStep
              : nextBoundaries[bIdx - 1] + boundaryStep;
          const upper =
            bIdx === nextBoundaries.length - 1
              ? boundaryMax - boundaryStep
              : nextBoundaries[bIdx + 1] - boundaryStep;
          nextBoundaries[bIdx] = Math.min(Math.max(candidate, lower), upper);
        }
        return { boundaries: nextBoundaries, rates: nextRates };
      });
    },
    [boundaryMin, boundaryMax, boundaryStep, rateMin, rateMax, rateStep],
  );

  // Format helpers for the live drag readout.
  const formatEuro = (v: number) => `€${Math.round(v).toLocaleString()}`;
  const formatRate = (v: number) =>
    `${(Math.round(v * 10000) / 100).toFixed(1)}%`;

  // Mirror cell format/parse pairs. Boundaries are raw integer euros;
  // rates are stored as fractions but displayed as percentages.
  // useCallback so the NumberCell's draft-resync effect only fires when
  // the canonical value changes, not on every parent re-render.
  const formatBoundaryDraft = useCallback(
    (v: number) => String(Math.round(v)),
    [],
  );
  const parseBoundaryDraft = useCallback((s: string) => Number(s), []);
  const formatRateDraft = useCallback(
    (v: number) => String(Math.round(v * 10000) / 100),
    [],
  );
  const parseRateDraft = useCallback((s: string) => Number(s) / 100, []);

  return (
    <div
      className="beamm-bracket-editor"
      data-testid={`bracket-editor-${inputId}`}
    >
      <div className="beamm-bracket-editor__label beamm-input-island__label">
        <span>{label}</span>
        {description ? <InfoIcon description={description} /> : null}
      </div>

      <svg
        className="beamm-bracket-editor__chart"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Marginal tax rate staircase for ${label}`}
      >
        {/* y-axis: percentage ticks + gridlines */}
        <g className="beamm-bracket-editor__axis">
          {yTicks.map((t) => (
            <g key={`y-${t}`} transform={`translate(0, ${yScale(t)})`}>
              <line
                x1={MARGIN.left}
                x2={WIDTH - MARGIN.right}
                className="beamm-bracket-editor__gridline"
              />
              <text
                x={MARGIN.left - 8}
                dy="0.32em"
                textAnchor="end"
                className="beamm-bracket-editor__tick"
              >
                {`${Math.round(t * 100)}%`}
              </text>
            </g>
          ))}
          <text
            x={4}
            y={MARGIN.top - 4}
            className="beamm-bracket-editor__axis-label"
          >
            {yAxisLabel}
          </text>
        </g>

        {/* x-axis: euro ticks */}
        <g className="beamm-bracket-editor__axis">
          <line
            x1={MARGIN.left}
            x2={WIDTH - MARGIN.right}
            y1={HEIGHT - MARGIN.bottom}
            y2={HEIGHT - MARGIN.bottom}
            className="beamm-bracket-editor__axis-line"
          />
          {xTicks.map((t) => (
            <g
              key={`x-${t}`}
              transform={`translate(${xScale(t)}, ${HEIGHT - MARGIN.bottom})`}
            >
              <line y2={4} className="beamm-bracket-editor__tickmark" />
              <text
                y={16}
                textAnchor="middle"
                className="beamm-bracket-editor__tick"
              >
                {`€${t.toLocaleString()}`}
              </text>
            </g>
          ))}
          <text
            x={WIDTH - MARGIN.right}
            y={HEIGHT - 4}
            textAnchor="end"
            className="beamm-bracket-editor__axis-label"
          >
            {xAxisLabel}
          </text>
        </g>

        {/* Baseline staircase: ghost grey under the bold reform line. */}
        <path
          className="beamm-bracket-editor__path beamm-bracket-editor__path--baseline"
          d={pathGen(baselinePoints) ?? ""}
        />
        {baselineHandlePositions.map((p, i) => (
          <circle
            key={`baseline-${i}`}
            className="beamm-bracket-editor__handle beamm-bracket-editor__handle--baseline"
            cx={xScale(p.x)}
            cy={yScale(p.y)}
            r={5}
          />
        ))}

        <path
          className="beamm-bracket-editor__path beamm-bracket-editor__path--reform"
          d={pathGen(reformPoints) ?? ""}
        />

        {/* Reform handles — d3-drag attaches here via gRef. */}
        <g ref={gRef}>
          {handlePositions.map((p, i) => (
            <circle
              key={`handle-${i}`}
              className={`beamm-bracket-editor__handle beamm-bracket-editor__handle--reform${
                activeHandle === i
                  ? " beamm-bracket-editor__handle--active"
                  : ""
              }`}
              data-testid={`bracket-editor-handle-${i}`}
              cx={xScale(p.x)}
              cy={yScale(p.y)}
              r={8}
              tabIndex={0}
              role="slider"
              aria-label={
                i === 0
                  ? `Marginal rate for the first bracket, currently ${formatRate(state.rates[0])}`
                  : `Boundary ${i} and rate above it, currently ${formatEuro(state.boundaries[i - 1])} / ${formatRate(state.rates[i])}`
              }
              aria-valuetext={
                i === 0
                  ? formatRate(state.rates[0])
                  : `${formatEuro(state.boundaries[i - 1])}, ${formatRate(state.rates[i])}`
              }
              onKeyDown={(e) => handleKeyDown(e, i)}
              onFocus={() => setActiveHandle(i)}
              onBlur={() => setActiveHandle(null)}
            />
          ))}
        </g>

        {/* Live readout next to the active handle. Anchored above the
            handle when there's room, below it when too close to the top
            of the chart. */}
        {activeHandle !== null
          ? (() => {
              const p = handlePositions[activeHandle];
              if (!p) return null;
              const cx = xScale(p.x);
              const cy = yScale(p.y);
              const above = cy > MARGIN.top + 24;
              const readoutText =
                activeHandle === 0
                  ? formatRate(state.rates[0])
                  : `${formatEuro(state.boundaries[activeHandle - 1])} · ${formatRate(state.rates[activeHandle])}`;
              return (
                <g className="beamm-bracket-editor__readout" aria-hidden="true">
                  <text
                    x={cx}
                    y={above ? cy - 14 : cy + 22}
                    textAnchor="middle"
                  >
                    {readoutText}
                  </text>
                </g>
              );
            })()
          : null}
      </svg>

      {nonMonotonicAt.length > 0 ? (
        <p
          className="beamm-bracket-editor__warning"
          data-testid="bracket-editor-warning"
          role="status"
        >
          Non-progressive at bracket {nonMonotonicAt.join(", ")}: the marginal
          rate steps down compared to the previous bracket.
        </p>
      ) : null}

      {/* Numeric mirror — same state as the chart; mirror inputs apply
          the same clamps the drag handlers apply. table-layout: fixed
          plus a colgroup keeps the bracket / boundary / rate columns
          uniform across all rows, including the bracket-4 row whose
          boundary cell carries an ∞ placeholder instead of an input. */}
      <table
        className="beamm-bracket-editor__mirror"
        data-testid="bracket-editor-mirror"
      >
        <colgroup>
          <col className="beamm-bracket-editor__mirror-col-bracket" />
          <col className="beamm-bracket-editor__mirror-col-boundary" />
          <col className="beamm-bracket-editor__mirror-col-rate" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Bracket</th>
            <th scope="col">Upper bound (€)</th>
            <th scope="col">Marginal rate (%)</th>
          </tr>
        </thead>
        <tbody>
          {state.rates.map((rate, i) => {
            const boundaryModified =
              i < state.boundaries.length &&
              state.boundaries[i] !== boundaryDefaults[i];
            const rateModified = state.rates[i] !== rateDefaults[i];
            return (
              <tr key={i}>
                <th scope="row">{i + 1}</th>
                <td>
                  <div className="beamm-bracket-editor__mirror-cell">
                    {i < state.boundaries.length ? (
                      <NumberCell
                        value={state.boundaries[i]}
                        min={boundaryMin}
                        max={boundaryMax}
                        format={formatBoundaryDraft}
                        parse={parseBoundaryDraft}
                        onCommit={(v) => onBoundaryCellChange(i, v)}
                        testId={`bracket-editor-boundary-${i}`}
                        ariaLabel={`Upper bound for bracket ${i + 1}`}
                      />
                    ) : (
                      <span
                        className="beamm-bracket-editor__mirror-na"
                        aria-label="No upper bound"
                      >
                        ∞
                      </span>
                    )}
                    {boundaryModified ? (
                      <span
                        className="beamm-bracket-editor__cell-modified"
                        title="Modified from default"
                        aria-label="Modified from default"
                        data-testid={`bracket-editor-boundary-modified-${i}`}
                      />
                    ) : null}
                  </div>
                </td>
                <td>
                  <div className="beamm-bracket-editor__mirror-cell">
                    <NumberCell
                      value={rate}
                      min={rateMin}
                      max={rateMax}
                      format={formatRateDraft}
                      parse={parseRateDraft}
                      onCommit={(v) => onRateCellChange(i, v)}
                      testId={`bracket-editor-rate-${i}`}
                      ariaLabel={`Marginal rate for bracket ${i + 1}`}
                    />
                    {rateModified ? (
                      <span
                        className="beamm-bracket-editor__cell-modified"
                        title="Modified from default"
                        aria-label="Modified from default"
                        data-testid={`bracket-editor-rate-modified-${i}`}
                      />
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {isModified ? (
        <button
          type="button"
          className="beamm-bracket-editor__reset beamm-input-island__reset"
          data-testid={`bracket-editor-reset-${inputId}`}
          onClick={resetAll}
        >
          <span aria-hidden="true">↺</span> Reset to defaults
        </button>
      ) : null}
    </div>
  );
}
